import React, { useCallback } from 'react'
import { Currency, Fraction, Pair } from 'vvs-sdk'
import { Button, ChevronDownIcon, Text, useModal, Flex } from 'vvs-uikit'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useTokenBalance } from 'state/wallet/hooks'
import LocalBalance from 'components/LocalBalance'
import { useCurrencyBalance } from '../../state/wallet/hooks'
import CurrencySearchModal from '../SearchModal/CurrencySearchModal'
import { CurrencyLogo, DoubleCurrencyLogo } from '../Logo'
import { AutoRow, RowBetween } from '../Layout/Row'
import { Input as NumericalInput } from './NumericalInput'

const InputContainer = styled.div<{ selected: boolean }>`
  padding: ${({ selected }) => (selected ? '0.75rem 0.5rem 0.75rem 1rem' : '0.75rem 0.75rem 0.75rem 1rem')};
`
const InputRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`
const CurrencySelectButton = styled(Button).attrs({ variant: 'text', scale: 'sm' })`
  padding: 0 0.5rem;
`
const CurrencySelectionDiv = styled.div<{ selected: boolean }>`
  cursor: default;
  padding: 0 0.5rem;
`
const LabelRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.75rem;
  line-height: 1rem;
  padding: 0.75rem 1rem 0 1rem;
`
const InputPanel = styled.div<{ hideInput?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  position: relative;
  border-radius: ${({ hideInput }) => (hideInput ? '8px' : '20px')};
  background-color: ${({ theme }) => theme.colors.inputBackground};
  z-index: 1;
`
const Container = styled.div<{ hideInput: boolean }>`
  border-radius: 16px;
`
const PercentageButton = styled(Button)`
  border-radius: 50px;
  width: 50px;
  height: 36px;
  padding: 9px 12px;
  margin: 0px 6px;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
`
const currencyComponent = {
  disabled: CurrencySelectionDiv,
  enabled: CurrencySelectButton,
}
interface CurrencyInputPanelProps {
  value: string
  onUserInput?: (value: string) => void
  onMax?: () => void
  showMaxButton: boolean
  label?: string
  onCurrencySelect?: (currency: Currency) => void
  onPairSelect?: (pair: Pair) => void
  currency?: Currency | null
  disableCurrencySelect?: boolean
  hideBalance?: boolean
  pair?: Pair | null
  hideInput?: boolean
  otherCurrency?: Currency | null
  id: string
  showCommonBases?: boolean
  showPercentageButtons?: boolean
  disabledInput?: boolean
  valueInUsdHint?: BigNumber
}

export default function CurrencyInputPanel({
  value,
  onUserInput,
  onMax,
  showMaxButton,
  label,
  onCurrencySelect,
  onPairSelect,
  currency,
  disableCurrencySelect = false,
  hideBalance = false,
  pair = null, // used for double token logo
  hideInput = false,
  otherCurrency,
  id,
  showCommonBases,
  showPercentageButtons,
  disabledInput = false,
  valueInUsdHint,
}: CurrencyInputPanelProps) {
  const { account } = useActiveWeb3React()
  const selectedCurrencyBalance = useCurrencyBalance(account ?? undefined, currency ?? undefined)
  const selectedPairBalance = useTokenBalance(account ?? undefined, pair?.liquidityToken ?? undefined)
  const { t } = useTranslation()
  const translatedLabel = label || t('Input')

  const CurrencyComponent = currencyComponent[disableCurrencySelect ? 'disabled' : 'enabled']

  const [onPresentCurrencyModal] = useModal(
    <CurrencySearchModal
      onCurrencySelect={onCurrencySelect}
      onPairSelect={onPairSelect}
      selectedPair={pair}
      selectedCurrency={currency}
      otherSelectedCurrency={otherCurrency}
      showCommonBases={showCommonBases}
    />,
  )

  const handleChangePercent = useCallback(
    (percent: string) => {
      if (selectedCurrencyBalance && currency && selectedCurrencyBalance.greaterThan('0')) {
        const fraction = new Fraction(percent, '100')
        const decimals = percent === '100' ? currency.decimals : Math.min(currency.decimals, 6)
        onUserInput(selectedCurrencyBalance.multiply(fraction).toFixed(decimals))
      }
    },
    [selectedCurrencyBalance, currency, onUserInput],
  )

  return (
    <InputPanel id={id}>
      <Container hideInput={hideInput}>
        {!hideInput && (
          <LabelRow>
            <RowBetween style={{ padding: '0' }}>
              <Text fontSize="14px">{translatedLabel}</Text>
              {account && (
                <Text
                  onClick={onMax}
                  fontSize="14px"
                  style={{ display: 'inline', cursor: 'pointer', textAlign: 'right' }}
                  ml={1}
                >
                  {!hideBalance &&
                    (currency
                      ? t('Balance: %balance%', {
                          balance:
                            selectedCurrencyBalance?.toFixed(selectedCurrencyBalance.currency.decimals) ?? t('Loading'),
                        })
                      : pair
                      ? t('Balance: %balance%', {
                          balance: selectedPairBalance?.toFixed() ?? t('Loading'),
                        })
                      : '-')}
                </Text>
              )}
            </RowBetween>
          </LabelRow>
        )}
        <InputContainer selected={disableCurrencySelect}>
          <InputRow style={hideInput ? { padding: '0', borderRadius: '8px' } : {}}>
            {!hideInput && (
              <>
                <NumericalInput
                  className="token-amount-input"
                  value={value}
                  onUserInput={(val) => {
                    onUserInput(val)
                  }}
                  disabled={disabledInput}
                />
                {account && currency && showMaxButton && label !== 'To' && (
                  <Button onClick={onMax} scale="sm" variant="text">
                    MAX
                  </Button>
                )}
              </>
            )}
            <CurrencyComponent
              selected={!!currency}
              className="open-currency-select-button"
              onClick={() => {
                if (!disableCurrencySelect) {
                  onPresentCurrencyModal()
                }
              }}
            >
              <Flex alignItems="center" justifyContent="space-between">
                {pair ? (
                  <DoubleCurrencyLogo currency0={pair.token0} currency1={pair.token1} size={16} margin />
                ) : currency ? (
                  <CurrencyLogo currency={currency} size="24px" style={{ marginRight: '8px' }} />
                ) : null}
                {pair ? (
                  <Text id="pair">
                    {pair?.token0.symbol}-{pair?.token1.symbol}
                  </Text>
                ) : onPairSelect ? (
                  <Text id="pair">{t('Select LP Pair')}</Text>
                ) : (
                  <Text id="pair">
                    {(currency && currency.symbol && currency.symbol.length > 20
                      ? `${currency.symbol.slice(0, 4)}...${currency.symbol.slice(
                          currency.symbol.length - 5,
                          currency.symbol.length,
                        )}`
                      : currency?.symbol) || t('Select Token')}
                  </Text>
                )}
                {!disableCurrencySelect && <ChevronDownIcon />}
              </Flex>
            </CurrencyComponent>
          </InputRow>
          {valueInUsdHint !== undefined && (
            <LocalBalance prefix="~ " color="textSubtle" fontSize="13px" value={valueInUsdHint} />
          )}
        </InputContainer>
        {showPercentageButtons && (
          <AutoRow justify="center" style={{ padding: '0 14px', margin: '14px 0 20px 0' }}>
            <PercentageButton onClick={() => handleChangePercent('25')}>25%</PercentageButton>
            <PercentageButton onClick={() => handleChangePercent('50')}>50%</PercentageButton>
            <PercentageButton onClick={() => handleChangePercent('75')}>75%</PercentageButton>
            <PercentageButton onClick={() => handleChangePercent('100')}>MAX</PercentageButton>
          </AutoRow>
        )}
      </Container>
    </InputPanel>
  )
}
